// Service Worker Registration for CompareMechanic PWA

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
  window.location.hostname === '[::1]' ||
  window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/)
);

/**
 * Register the service worker
 * @param {Object} config - Configuration options
 * @param {Function} config.onSuccess - Called when SW is registered successfully
 * @param {Function} config.onUpdate - Called when a new SW update is available
 * @param {Function} config.onOffline - Called when the app goes offline
 * @param {Function} config.onOnline - Called when the app comes back online
 */
export function register(config = {}) {
  if ('serviceWorker' in navigator) {
    const publicUrl = new URL(process.env.PUBLIC_URL || '', window.location.href);

    // Don't register if PUBLIC_URL is on a different origin
    if (publicUrl.origin !== window.location.origin) {
      console.warn('[SW] PUBLIC_URL is on a different origin. Not registering service worker.');
      return;
    }

    window.addEventListener('load', () => {
      const swUrl = `${process.env.PUBLIC_URL || ''}/sw.js`;

      if (isLocalhost) {
        // In localhost, check if SW exists and register
        checkValidServiceWorker(swUrl, config);
        navigator.serviceWorker.ready.then(() => {
          console.log('[SW] Running in localhost with service worker');
        });
      } else {
        // In production, register the service worker
        registerValidSW(swUrl, config);
      }
    });

    // Set up online/offline listeners
    setupConnectivityListeners(config);
  } else {
    console.warn('[SW] Service workers are not supported in this browser');
  }
}

function registerValidSW(swUrl, config) {
  navigator.serviceWorker
    .register(swUrl)
    .then((registration) => {
      console.log('[SW] Service worker registered:', registration.scope);

      // Check for updates immediately on registration
      registration.update();

      // Check for updates periodically (every 5 minutes for faster updates)
      setInterval(() => {
        registration.update();
      }, 5 * 60 * 1000);

      registration.onupdatefound = () => {
        const installingWorker = registration.installing;

        if (installingWorker == null) {
          return;
        }

        installingWorker.onstatechange = () => {
          if (installingWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              // New content is available
              console.log('[SW] New content available; please refresh.');

              if (config && config.onUpdate) {
                config.onUpdate(registration);
              }
            } else {
              // Content is cached for offline use
              console.log('[SW] Content cached for offline use.');

              if (config && config.onSuccess) {
                config.onSuccess(registration);
              }
            }
          }
        };
      };
    })
    .catch((error) => {
      console.error('[SW] Error during service worker registration:', error);
    });
}

function checkValidServiceWorker(swUrl, config) {
  // Check if the service worker can be found
  fetch(swUrl, { headers: { 'Service-Worker': 'script' } })
    .then((response) => {
      const contentType = response.headers.get('content-type');

      if (response.status === 404 || (contentType != null && contentType.indexOf('javascript') === -1)) {
        // No service worker found, unregister and reload
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister().then(() => {
            window.location.reload();
          });
        });
      } else {
        // Service worker found, register it
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => {
      console.log('[SW] No internet connection. App running in offline mode.');
    });
}

function setupConnectivityListeners(config) {
  window.addEventListener('online', () => {
    console.log('[SW] App is back online');
    if (config && config.onOnline) {
      config.onOnline();
    }
  });

  window.addEventListener('offline', () => {
    console.log('[SW] App is offline');
    if (config && config.onOffline) {
      config.onOffline();
    }
  });
}

/**
 * Unregister the service worker
 */
export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
        console.log('[SW] Service worker unregistered');
      })
      .catch((error) => {
        console.error('[SW] Error unregistering service worker:', error);
      });
  }
}

/**
 * Send a message to the service worker
 * @param {Object} message - Message to send
 */
export function sendMessage(message) {
  if (navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage(message);
  }
}

/**
 * Skip waiting and activate new service worker
 */
export function skipWaiting() {
  sendMessage({ type: 'SKIP_WAITING' });
}

/**
 * Clear all service worker caches
 */
export function clearCache() {
  sendMessage({ type: 'CLEAR_CACHE' });
}

/**
 * Clear only API cache (useful for logout or data refresh)
 */
export function clearAPICache() {
  sendMessage({ type: 'CLEAR_API_CACHE' });
}

/**
 * Invalidate specific cache entries by URL patterns
 * @param {string[]} patterns - URL patterns to match and invalidate
 */
export function invalidateCache(patterns) {
  if (patterns && patterns.length > 0) {
    sendMessage({ type: 'INVALIDATE_CACHE', patterns });
  }
}

/**
 * Invalidate cache for common data types
 */
export const invalidateCacheFor = {
  customers: () => invalidateCache(['customers']),
  quotes: () => invalidateCache(['quotes', 'quote_requests']),
  jobs: () => invalidateCache(['jobs']),
  appointments: () => invalidateCache(['appointments']),
  invoices: () => invalidateCache(['invoices']),
  garages: () => invalidateCache(['garages']),
  services: () => invalidateCache(['master_services', 'garage_services']),
};

/**
 * Check if the app is currently online
 * @returns {boolean}
 */
export function isOnline() {
  return navigator.onLine;
}

/**
 * Get the current service worker registration
 * @returns {Promise<ServiceWorkerRegistration|null>}
 */
export async function getRegistration() {
  if ('serviceWorker' in navigator) {
    return navigator.serviceWorker.ready;
  }
  return null;
}
